import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Lock } from "lucide-react";

export default function ResearchDetail() {
  const { researchId } = useParams();
  const { authAxios } = useAuth();
  const { tr, lang } = useLang();
  const [r, setR] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [concentration, setConcentration] = useState("fine_parfum");

  // Monotonic "generation" counter. Every URL/locale change bumps this
  // counter; every async callback captures the counter value at fetch
  // time and refuses to update React state when the counter no longer
  // matches. This guarantees that an in-flight response for a previous
  // research (or previous locale) can NEVER overwrite the currently
  // displayed research's AI interpretation.
  const genRef = useRef(0);

  useEffect(() => {
    genRef.current += 1;
    const myGen = genRef.current;

    // Synchronously clear ALL research-scoped state.
    setR(null);
    setAi(null);
    setAiError(null);
    setAiBusy(false);

    // Load the currently requested research (with active concentration).
    (async () => {
      try {
        const res = await authAxios.get(`/research/${researchId}?concentration=${concentration}`);
        if (myGen !== genRef.current) return;
        setR(res.data);
      } catch (e) {
        if (myGen !== genRef.current) return;
        toast.error(tr("p5_research_not_found") || "Research not found");
      }
    })();

    // Load the cached AI interpretation ONLY for this research + locale.
    (async () => {
      try {
        const ac = await authAxios.get(
          `/ai/research/${researchId}/interpret?locale=${lang || "en"}`
        );
        if (myGen !== genRef.current) return;
        setAi(ac.data);
      } catch (e) {
        if (myGen !== genRef.current) return;
        setAi(null);
      }
    })();
  }, [authAxios, researchId, lang, tr]);

  // Convenience refetch used by the unlock button + concentration switch.
  const loadResearch = async () => {
    try {
      const res = await authAxios.get(`/research/${researchId}?concentration=${concentration}`);
      setR(res.data);
    } catch (_) {}
  };

  // Refetch (but do NOT reset the AI state) when concentration changes.
  useEffect(() => {
    if (!r) return;
    loadResearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concentration]);

  const askAi = async (regenerate = false) => {
    const myGen = genRef.current;
    const requestedId = researchId;
    setAiBusy(true);
    setAiError(null);
    try {
      const qs = `?locale=${lang || "en"}${regenerate ? "&regenerate=true" : ""}`;
      const res = await authAxios.post(`/ai/research/${requestedId}/interpret${qs}`);
      if (myGen !== genRef.current) return; // navigation happened
      setAi(res.data);
      toast.success(tr("p5_ai_done"));
    } catch (e) {
      if (myGen !== genRef.current) return;
      const detail = e?.response?.data?.detail || tr("p5_ai_unavailable");
      setAiError(detail);
      toast.error(detail);
    }
    if (myGen === genRef.current) setAiBusy(false);
  };

  if (!r) return (
    <div className="min-h-screen flex flex-col"><Header /><main className="flex-1 max-w-4xl mx-auto w-full px-6 py-16 text-[#a9a9b8]">{tr("loading")}</main></div>
  );

  const unlocked = r.official_formula?.unlocked === true;
  const characterProfile = r.character_profile || {};

  // Anchor guard: only trust an AI interpretation whose public_id
  // matches the currently displayed research. Any drift (stale in-flight
  // response, cache mismatch, etc.) is treated as "no interpretation
  // yet" for this research.
  const aiForThisResearch = ai && ai.public_id && ai.public_id === r.public_id ? ai : null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-10">
        <div className="pfi-overline">PERSONA 5.0 · {tr("p5_research_detail")}</div>
        <div className="mt-3 flex items-baseline gap-3 flex-wrap">
          <h1 className="font-serif text-3xl sm:text-4xl" data-testid="research-detail-title">
            {r.identity?.name}
          </h1>
          <span className="text-xs font-mono text-[#e6c687]" data-testid="research-public-id">
            {r.public_id}
          </span>
        </div>
        <p className="mt-2 text-xs font-mono text-[#6c6c7a]">
          {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
        </p>

        {/* Identity + Character Profile */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Block title={tr("p5_research_normalized")}>
            <Row k={tr("p5_form_dob")} v={r.identity?.birth_date} />
            <Row k={tr("p5_form_blood")} v={r.identity?.blood_type} />
            <Row k={tr("p5_form_gender")} v={r.identity?.gender} />
            {/* fragrance_path (PATH_A/PATH_B) is internal — not shown */}
          </Block>

          {/* Character Profile — unified consumer-facing personality prose.
              No letter, no per-section labels ("Name character",
              "Birth-month character", etc.), no digital sums. */}
          <Block title={tr("p5_character_profile")}>
            {characterProfile.description ? (
              <div className="text-sm text-[#e6c687] italic leading-relaxed" data-testid="character-profile-desc">
                “{characterProfile.description}”
              </div>
            ) : (
              <div className="text-xs font-mono text-[#6c6c7a]" data-testid="character-profile-empty">
                {tr("p5_character_profile_empty") || "Character profile pending."}
              </div>
            )}
          </Block>
        </div>

        {/* 6-slot Fragrance Identity (consumer wording only — no T-labels) */}
        {r.fragrance_identity?.length ? (
          <div className="mt-6 pfi-card p-6" data-testid="fragrance-identity-block">
            <div className="pfi-overline">{tr("p5_fragrance_identity")}</div>
            <p className="mt-1 text-[11px] font-mono text-[#6c6c7a]">{tr("p5_fragrance_identity_sub")}</p>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {r.fragrance_identity.map((s, i) => (
                <div key={i} className="rounded-xl border border-white/10 p-3" data-testid={`identity-slot-${i}`}>
                  <div className="pfi-overline text-[#e6c687]">{s.aroma_family}</div>
                  <div className="mt-1 font-serif text-lg text-[#f8f8fc]" data-testid={`identity-aroma-${i}`}>{s.aroma_name}</div>
                  <div className="mt-1 text-[11px] font-mono text-[#a9a9b8]" data-testid={`identity-status-${i}`}>
                    {s.verification_status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Pyramid — derived from the SAME six canonical aromas of this research */}
        {r.pyramid && (r.pyramid.top?.length || r.pyramid.heart?.length || r.pyramid.base?.length || r.pyramid.unclassified?.length) ? (
          <div className="mt-6 pfi-card p-6" data-testid="pyramid-result-block">
            <div className="pfi-overline">{tr("p5_research_pyramid")}</div>
            <div className="mt-4 space-y-2">
              <PyramidRow label="TOP" testid="pyr-top" notes={r.pyramid.top} width="60%" tint="rgba(230,198,135,0.20)" />
              <PyramidRow label="HEART" testid="pyr-heart" notes={r.pyramid.heart} width="80%" tint="rgba(212,175,55,0.28)" />
              <PyramidRow label="BASE" testid="pyr-base" notes={r.pyramid.base} width="100%" tint="rgba(200,138,53,0.35)" />
            </div>
            {r.pyramid.unclassified?.length ? (
              <div className="mt-4 rounded-lg border border-white/10 bg-[#181820]/60 p-3" data-testid="pyr-unclassified">
                <div className="pfi-overline text-[#a9a9b8]">
                  {tr("p5_pyramid_pending") || "Classification pending"}
                </div>
                <div className="mt-2 text-sm text-[#f8f8fc] flex flex-wrap gap-x-2 gap-y-1">
                  {r.pyramid.unclassified.map((n, i) => (
                    <span key={i} data-testid={`pyr-unclassified-item-${i}`}>
                      {n}{i < r.pyramid.unclassified.length - 1 ? <span className="text-[#6c6c7a]"> · </span> : null}
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-[11px] font-mono text-[#6c6c7a]">
                  {tr("p5_pyramid_pending_hint") || "Professional olfactive position pending for these legacy aromas."}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* OFFICIAL PERSONA FORMULA — Build 1 FREE unlock + concentration.
            NEVER exposes T1..T6, formula_vector, formula_total, fingerprint,
            digital sums, PATH_A, or any internal algorithm detail. */}
        <div className="mt-6 pfi-card p-6 border border-[#d4af37]/25" data-testid="official-formula-block">
          <div className="pfi-overline inline-flex items-center gap-2">
            {unlocked ? <Sparkles size={12} className="text-[#e6c687]" /> : <Lock size={12} />}
            {tr("p5_official_formula")}
          </div>
          <div className="mt-3 text-sm text-[#f8f8fc]" data-testid="official-formula-message">
            {r.official_formula?.message}
          </div>
          {!unlocked ? (
            <div className="mt-4">
              <button
                data-testid="unlock-cta-btn"
                className="pfi-btn-gold rounded-full px-5 py-2.5 text-sm disabled:opacity-60"
                disabled={unlockBusy}
                onClick={async () => {
                  setUnlockBusy(true);
                  try {
                    await authAxios.post(`/research/${researchId}/unlock-formula-free`);
                    await loadResearch();
                    toast.success(tr("p5_unlock_done") || "Formula unlocked");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Unlock failed");
                  } finally { setUnlockBusy(false); }
                }}
              >
                {unlockBusy ? "…" : tr("p5_unlock_cta")}
              </button>
              <p className="mt-2 text-[11px] font-mono text-[#6c6c7a]">{tr("p5_unlock_helper")}</p>
            </div>
          ) : (
            <div className="mt-4 space-y-4" data-testid="official-formula-unlocked">
              <div className="flex flex-wrap gap-2" data-testid="concentration-selector">
                {(r.official_formula?.concentrations_available || ["fine_parfum","edp","exdp","esdp"]).map((c) => (
                  <button
                    key={c}
                    data-testid={`concentration-${c}`}
                    onClick={() => setConcentration(c)}
                    className={`rounded-full px-4 py-1.5 text-xs border transition ${
                      concentration === c
                        ? "bg-[#d4af37]/20 border-[#d4af37]/60 text-[#f8f8fc]"
                        : "border-white/10 text-[#a9a9b8] hover:border-[#d4af37]/40"
                    }`}
                  >
                    {c === "fine_parfum" ? "Fine Parfum" : c.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="rounded-lg border border-white/10 bg-[#181820]/60 p-4">
                <div className="flex justify-between text-xs font-mono text-[#a9a9b8] mb-3">
                  <span data-testid="fo-share">FO {r.official_formula?.formula?.fo_percentage}%</span>
                  <span data-testid="solvent-share">Solvent {r.official_formula?.formula?.solvent_percentage}%</span>
                </div>
                <ul className="space-y-1.5" data-testid="formula-materials">
                  {(r.official_formula?.formula?.materials || []).map((m, i) => (
                    <li key={i} data-testid={`material-${i}`} className="flex justify-between text-sm text-[#f8f8fc]">
                      <span>{m.aroma_name}<span className="text-[#6c6c7a] text-xs"> · {m.aroma_family}</span></span>
                      <span className="font-mono text-[#e6c687]">{m.percentage}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* PERSONA AI Perfumer (Build 04 + Build 05 locale) */}
        <div className="mt-8 pfi-card p-6 border border-[#d4af37]/25" data-testid="ai-interpretation-block">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="pfi-overline inline-flex items-center gap-2 text-[#e6c687]">
                <Sparkles size={12} /> {tr("p5_ai_title")}
              </div>
              <h2 className="mt-1 font-serif text-2xl">{tr("p5_ai_subtitle")}</h2>
              <p className="mt-1 text-xs font-mono text-[#6c6c7a] max-w-2xl leading-relaxed">
                {tr("p5_ai_disclaimer")}
              </p>
            </div>
            {aiForThisResearch ? (
              <button data-testid="ai-regenerate-btn" onClick={() => askAi(true)} disabled={aiBusy}
                className="pfi-btn-outline rounded-full px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-60">
                <RefreshCw size={12} className={aiBusy ? "animate-spin" : ""} /> {tr("p5_ai_regenerate")}
              </button>
            ) : null}
          </div>

          {!aiForThisResearch && !aiBusy ? (
            <div className="mt-6">
              <button data-testid="ai-ask-btn" onClick={() => askAi(false)}
                className="pfi-btn-gold rounded-full px-6 py-3 text-sm inline-flex items-center gap-2">
                <Sparkles size={14} /> {tr("p5_ai_cta")}
              </button>
              {aiError ? (<div className="mt-3 text-xs text-[#f7b1a5]" data-testid="ai-error">{aiError}</div>) : null}
            </div>
          ) : null}

          {aiBusy ? (
            <div className="mt-6 space-y-2 animate-pulse" data-testid="ai-loading">
              <div className="h-3 bg-white/5 rounded w-2/3"></div>
              <div className="h-3 bg-white/5 rounded w-4/5"></div>
              <div className="h-3 bg-white/5 rounded w-1/2"></div>
              <div className="mt-3 text-xs font-mono text-[#a9a9b8]">{tr("p5_ai_generating")}</div>
            </div>
          ) : null}

          {aiForThisResearch && !aiBusy ? (
            <div className="mt-6 space-y-5" data-testid="ai-result" data-ai-public-id={aiForThisResearch.public_id}>
              <AiSection title={tr("p5_ai_summary")} testid="ai-summary" body={aiForThisResearch.summary} />
              <AiSection title={tr("p5_ai_character")} testid="ai-character" body={aiForThisResearch.character_interpretation} />
              <AiSection title={tr("p5_ai_identity")} testid="ai-identity" body={aiForThisResearch.fragrance_identity_interpretation} />
              <AiSection title={tr("p5_ai_pyramid")} testid="ai-pyramid" body={aiForThisResearch.pyramid_interpretation} />
              <AiSection title={tr("p5_ai_mood")} testid="ai-mood" body={aiForThisResearch.scent_mood} />
              {aiForThisResearch.wearing_context?.length ? (
                <div data-testid="ai-wearing">
                  <div className="pfi-overline">{tr("p5_ai_wearing")}</div>
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {aiForThisResearch.wearing_context.map((w, i) => (
                      <li key={i} className="rounded-full border border-white/10 px-3 py-1 text-xs text-[#a9a9b8]">{w}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <AiSection title={tr("p5_ai_perfumer_note")} testid="ai-note" body={aiForThisResearch.perfumer_note} italic />
              {aiForThisResearch.data_limitations?.length ? (
                <div className="mt-4 rounded-lg border border-[#d4af37]/25 bg-[#181820]/60 p-3" data-testid="ai-limitations">
                  <div className="pfi-overline text-[#e6c687]">{tr("p5_ai_limitations")}</div>
                  <ul className="mt-2 list-disc pl-5 text-xs text-[#a9a9b8] space-y-0.5">
                    {aiForThisResearch.data_limitations.map((l, i) => (<li key={i}>{l}</li>))}
                  </ul>
                </div>
              ) : null}
              <div className="mt-4 text-[10px] font-mono text-[#6c6c7a]" data-testid="ai-footer">
                {tr("p5_ai_footer")} · {aiForThisResearch.locale?.toUpperCase() || "EN"} · {aiForThisResearch.cached ? tr("p5_ai_cached") : tr("p5_ai_fresh")}
              </div>
            </div>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="pfi-card p-6">
      <div className="pfi-overline">{title}</div>
      <div className="mt-4 space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-[11px] font-mono text-[#6c6c7a]">{k}</span>
      <span className="text-[#f8f8fc]">{String(v ?? "—")}</span>
    </div>
  );
}

function AiSection({ title, body, testid, italic }) {
  if (!body) return null;
  return (
    <div data-testid={testid}>
      <div className="pfi-overline">{title}</div>
      <p className={"mt-2 text-sm leading-relaxed " + (italic ? "italic text-[#e6c687]" : "text-[#f8f8fc]")}>{body}</p>
    </div>
  );
}

function PyramidRow({ label, notes, width, tint, testid }) {
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="relative rounded-xl border border-[#d4af37]/25 px-4 py-3 min-h-[56px] flex items-center justify-center"
        style={{ width, background: `linear-gradient(180deg, ${tint} 0%, transparent 100%)` }}>
        <div className="text-center">
          <div className="pfi-overline">{label}</div>
          <div className="mt-1 text-sm text-[#f8f8fc] flex flex-wrap gap-x-2 justify-center">
            {(notes || []).length ? notes.map((n, i) => (
              <span key={i}>{n}{i < notes.length - 1 ? <span className="text-[#6c6c7a]"> · </span> : null}</span>
            )) : <span className="text-[#6c6c7a] text-xs">—</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
